import { memo, useMemo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { Info } from "lucide-react";
import type { StockAvailabilityResponseDTO } from "@/features/stocks/types/stock.types";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { RentalType } from "@/shared/types/enums";
import { StockBarcodeAdder } from "../../StockBarcodeAdder";
import { SelectedStocksList } from "../../SelectedStocksList";
import { GownItemsField } from "../../GownItemsField";
import type { RentalCreateFormSchema } from "../../rental-create-schema";
import { StepHeading } from "./StepHeading";

interface StepItemsProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  stockItems: StockAvailabilityResponseDTO[];
  onStockAdd: (item: StockAvailabilityResponseDTO) => void;
  onStockRemove: (stockId: number) => void;
}

/**
 * Etapa 4 — peças (SUIT) ou itens de beca (GOWN).
 *
 * Etapa **opcional**: o backend aceita um aluguel sem peças/itens (fica
 * incompleto e pode ser completado depois). Por isso não há validação
 * bloqueante aqui — o banner deixa isso explícito.
 */
export const StepItems = memo(function StepItems({
  form,
  stockItems,
  onStockAdd,
  onStockRemove,
}: StepItemsProps) {
  const rentalType = useWatch({ control: form.control, name: "rentalType" });
  const isSuit = rentalType === RentalType.SUIT;

  return (
    <div className="space-y-6">
      <StepHeading
        title={isSuit ? "Peças do aluguel" : "Itens de beca"}
        description="Adicione agora ou deixe para depois."
      />

      <div className="flex items-start gap-2 rounded-md border border-input bg-muted/40 p-3 text-xs text-muted-foreground">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <p>
          Esta etapa é opcional. Você pode salvar o aluguel sem
          {isSuit ? " peças" : " itens"} — ele ficará incompleto e poderá ser
          completado depois.
        </p>
      </div>

      {isSuit ? (
        <SuitSection
          form={form}
          stockItems={stockItems}
          onStockAdd={onStockAdd}
          onStockRemove={onStockRemove}
        />
      ) : (
        <GownSection form={form} />
      )}
    </div>
  );
});

function GownSection({
  form,
}: {
  form: UseFormReturn<RentalCreateFormSchema>;
}) {
  const gownItems = useWatch({ control: form.control, name: "gownItems" });
  return (
    <GownItemsField
      items={gownItems}
      onChange={(items) =>
        form.setValue("gownItems", items, { shouldDirty: true })
      }
    />
  );
}

interface SuitSectionProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  stockItems: StockAvailabilityResponseDTO[];
  onStockAdd: (item: StockAvailabilityResponseDTO) => void;
  onStockRemove: (stockId: number) => void;
}

function SuitSection({
  form,
  stockItems,
  onStockAdd,
  onStockRemove,
}: SuitSectionProps) {
  const hasShoes = useWatch({ control: form.control, name: "hasShoes" });
  const stockIds = useWatch({ control: form.control, name: "stockIds" });
  const expectedOutAt = useWatch({
    control: form.control,
    name: "expectedOutAt",
  });
  const expectedInAt = useWatch({
    control: form.control,
    name: "expectedInAt",
  });

  const orderedItems = useMemo(
    () =>
      stockIds
        .map((id) => stockItems.find((item) => item.stockId === id))
        .filter((item): item is StockAvailabilityResponseDTO => Boolean(item)),
    [stockIds, stockItems],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4 items-end">
        <div className="space-y-1">
          <Label htmlFor="has-shoes">Sapatos</Label>
          <select
            id="has-shoes"
            value={hasShoes ? "yes" : "no"}
            onChange={(e) =>
              form.setValue("hasShoes", e.target.value === "yes", {
                shouldDirty: true,
              })
            }
            className="flex h-9 w-32 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          >
            <option value="no">Não</option>
            <option value="yes">Sim</option>
          </select>
        </div>
        {hasShoes ? (
          <FormField
            control={form.control}
            name="shoesPairQuantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quantidade de pares</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    value={field.value || ""}
                    onChange={(e) =>
                      field.onChange(Number(e.target.value) || 0)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
      </div>

      <StockBarcodeAdder
        expectedOutAt={expectedOutAt}
        expectedInAt={expectedInAt}
        existingStockIds={stockIds}
        onAdd={onStockAdd}
      />

      <SelectedStocksList items={orderedItems} onRemove={onStockRemove} />
    </div>
  );
}
